import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { vendorToolbagSummary, vendorProducts, vendorToggleProductStock } from "@/lib/vendorApi";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import ShopStatusCard from "@/components/vendor/ShopStatusCard";
import ShopProfileEditor from "@/components/vendor/ShopProfileEditor";
import MyServices from "@/components/vendor/MyServices";
import VendorAnalytics from "@/components/vendor/VendorAnalytics";
import { VendorAdsList } from "@/components/vendor/AdvertiseDialog";
import { useAdStrings } from "@/lib/adStrings";
import { shopPublicUrl, copyShopLink, PosterStudio } from "@/components/vendor/ShopLinkTools";
import ReviewsSection from "@/components/ReviewsSection";
import PrivacySettings from "@/components/profile/PrivacySettings";
import JobsManager from "@/components/activities/JobsManager";
import {
  Briefcase, Plus, Package, Wrench, Store, MapPin, Megaphone, BarChart3,
  MessageCircle, Star, UserCircle, ArrowRightLeft, Power, ChevronRight, Loader2, Boxes, Copy, QrCode, ShieldCheck, Eye, BriefcaseBusiness, ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const QuickBtn = ({ icon: Icon, label, badge, onClick, testid, active }) => (
  <button onClick={onClick} aria-expanded={active} className={`relative flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 min-h-[76px] text-center transition-colors ${active ? "border-emerald-400 bg-emerald-100" : "border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100/70"}`} data-testid={testid}>
    <Icon className="w-5 h-5 text-emerald-800" />
    <span className={`text-xs font-bold leading-tight ${active ? "text-emerald-800" : "text-slate-900"}`}>{label}</span>
    {badge != null && badge !== "" && <span className="absolute top-1.5 right-1.5 text-[10px] font-bold rounded-full bg-amber-500 text-white px-1.5 py-0.5">{badge}</span>}
  </button>
);

const Row = ({ icon: Icon, label, hint, onClick, testid }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-2.5 py-2 min-h-[48px] rounded-xl hover:bg-white/80 text-left transition-colors" data-testid={testid}>
    <span className="w-8 h-8 rounded-lg bg-white border border-emerald-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-emerald-800" /></span>
    <span className="flex-1 min-w-0 text-sm font-bold text-slate-900">{label}</span>
    {hint != null && hint !== "" && <span className="text-[11px] font-bold text-emerald-800 whitespace-nowrap">{hint}</span>}
    <ChevronRight className="w-4 h-4 text-emerald-700/60 flex-shrink-0 rtl-flip" />
  </button>
);

const Expand = ({ id, open, onToggle, icon: Icon, label, hint, badge, children }) => (
  <div data-testid={`tb-panel-${id}`}>
    <button onClick={() => onToggle(id)} aria-expanded={open} className={`w-full flex items-center gap-3 px-2.5 py-2 min-h-[48px] rounded-xl text-left transition-colors ${open ? "bg-white" : "hover:bg-white/80"}`} data-testid={`tb-${id}`}>
      <span className="w-8 h-8 rounded-lg bg-white border border-emerald-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-emerald-800" /></span>
      <span className={`flex-1 min-w-0 text-sm font-bold ${open ? "text-emerald-800" : "text-slate-900"}`}>{label}</span>
      {badge ? <span className="text-[10px] font-bold rounded-full bg-amber-500 text-white px-2 py-0.5 whitespace-nowrap" data-testid={`tb-${id}-badge`}>{badge}</span> : null}
      {hint != null && hint !== "" && <span className="text-[11px] font-bold text-emerald-800 whitespace-nowrap">{hint}</span>}
      <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-90 text-emerald-800" : "text-emerald-700/60 rtl-flip"}`} />
    </button>
    {open && <div className="mt-1 rounded-xl bg-white px-1 pt-2 pb-1 min-w-0" data-testid={`tb-panel-body-${id}`}>{children}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="border border-emerald-200 rounded-2xl p-2 bg-emerald-50 space-y-0.5" data-testid={`tb-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-emerald-800 px-2.5 pt-1.5 pb-1">{title}</div>
    {children}
  </div>
);

function QuickStock({ open, onChanged }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!open) return;
    vendorProducts().then(setItems).catch(() => setItems([]));
  }, [open]);
  const toggle = async (p) => {
    const next = p.in_stock === false;
    setBusy(p.id);
    try {
      await vendorToggleProductStock(p.id, next);
      setItems((list) => list.map((x) => (x.id === p.id ? { ...x, in_stock: next } : x)));
      toast.success(`${p.name} — ${next ? "Available" : "Unavailable"}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(null); }
  };
  if (!items) return <div className="text-xs text-slate-500 px-3 py-3">Loading...</div>;
  if (items.length === 0) return <div className="text-xs text-slate-500 px-3 py-3">No products yet.</div>;
  return (
    <div className="max-h-72 overflow-y-auto" data-testid="tb-quick-stock">
      {items.map((p) => {
        const on = p.in_stock !== false;
        return (
          <div key={p.id} className="flex items-center gap-3 px-3 py-2" data-testid={`tb-stock-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800 truncate">{p.name}</div>
              <div className={`text-[11px] font-semibold ${on ? "text-emerald-700" : "text-rose-600"}`} data-testid={`tb-stock-label-${p.id}`}>{on ? "Available" : "Unavailable"}</div>
            </div>
            <button
              type="button" onClick={() => toggle(p)} disabled={busy === p.id} aria-pressed={on} aria-label={`Toggle ${p.name}`}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors disabled:opacity-60 ${on ? "bg-emerald-600" : "bg-slate-300"}`}
              data-testid={`tb-stock-toggle-${p.id}`}
            >
              <span className={`pointer-events-none inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0"}`}>
                {busy === p.id && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function VendorToolBag({ open, onOpenChange, shopId, shops = [], act, onStockChanged, initialPanel, returnTo }) {
  const AM = useAdStrings().mgr;
  const [sum, setSum] = useState(null);
  const [panel, setPanel] = useState(null);
  const [tick, setTick] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeShop = shops.find((s) => s.shop_id === shopId);
  const slug = activeShop ? activeShop.slug || activeShop.shop_id : null;

  useEffect(() => {
    if (!open) { setPanel(null); return; }
    vendorToolbagSummary(shopId).then(setSum).catch(() => setSum(null));
  }, [open, shopId, tick]);

  useEffect(() => {
    if (open && initialPanel) reveal(initialPanel);
    // eslint-disable-next-line
  }, [open, initialPanel]);

  const changed = () => { setTick((n) => n + 1); onStockChanged?.(); };
  const toggle = (id) => setPanel((cur) => (cur === id ? null : id));
  const reveal = (id, target) => {
    setPanel(id);
    setTimeout(() => document.querySelector(`[data-testid="vendor-toolbag"] [data-testid="${target || `tb-panel-${id}`}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  };
  const ex = (id) => ({ id, open: panel === id, onToggle: toggle });

  const go = (action) => { onOpenChange(false); act(action); };
  const shopLabel = sum?.shop_status ? (sum.shop_status.is_open ? "Open" : "Closed") : "";
  const rating = sum?.rating ? `${sum.rating} ★${sum.reviews ? ` · ${sum.reviews}` : ""}` : "";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-slate-50 p-4" data-testid="vendor-toolbag">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl flex items-center gap-2"><Briefcase className="w-5 h-5 text-emerald-700" /> {t("tb.title")}</SheetTitle>
          <p className="text-xs text-slate-500">{t("tb.subtitle")}</p>
        </SheetHeader>

        <div className="grid grid-cols-3 gap-2 mt-4" data-testid="toolbag-quick-actions">
          <QuickBtn icon={Plus} label={t("tb.addProduct")} onClick={() => go("add-product")} testid="tb-quick-add-product" />
          <QuickBtn icon={Plus} label={t("tb.addService")} onClick={() => go("add-service")} testid="tb-quick-add-service" />
          <QuickBtn icon={Power} label={shopLabel ? `Shop: ${shopLabel}` : t("tb.shopStatus")} onClick={() => reveal("shop-status")} active={panel === "shop-status"} testid="tb-quick-shop-status" />
          <QuickBtn icon={Megaphone} label={t("tb.advertise")} onClick={() => go("advertise")} testid="tb-quick-advertise" />
          <QuickBtn icon={BarChart3} label={t("vendor.analytics")} onClick={() => reveal("analytics")} active={panel === "analytics"} testid="tb-quick-analytics" />
          <QuickBtn icon={ShieldCheck} label="Privacy & Notification Settings" active={panel === "privacy-settings"} onClick={() => toggle("privacy-settings")} testid="tb-quick-privacy-settings" />
        </div>
        {panel === "privacy-settings" && (
          <div className="mt-3 border border-emerald-100 rounded-2xl bg-white p-2" data-testid="tb-panel-privacy-settings">
            {returnTo && returnTo.startsWith("/v/") && (
              <button onClick={() => { onOpenChange(false); navigate(returnTo); }} className="vb-nav w-full mb-1 rounded-xl border border-emerald-200 px-3 py-2 text-sm text-left" data-testid="tb-back-to-customer-view">← Back to Customer View</button>
            )}
            <PrivacySettings embedded />
          </div>
        )}

        <div className="space-y-3 mt-4">
          <Section title="Products">
            <Row icon={Plus} label="Add Product" onClick={() => go("add-product")} testid="tb-add-product" />
            <Row icon={Package} label="My Products · edit, images, availability" hint={sum ? sum.products : ""} onClick={() => go("products")} testid="tb-my-products" />
          </Section>
          <Section title="Quick Stock">
            <Expand {...ex("stock-folder")} icon={Boxes} label="Quick Stock · Available / Unavailable" hint={sum ? sum.products : ""} badge={sum?.products_unavailable > 0 ? `${sum.products_unavailable} unavailable` : null}>
              <div className="flex items-center gap-2 px-3 pb-1 text-[11px] text-slate-500"><Boxes className="w-3.5 h-3.5 text-emerald-700" /> Tap to switch Available / Unavailable</div>
              <QuickStock open={open && panel === "stock-folder"} onChanged={changed} />
            </Expand>
          </Section>
          <Section title="Services">
            <Row icon={Plus} label="Add Service" onClick={() => go("add-service")} testid="tb-add-service" />
            <Expand {...ex("my-services")} icon={Wrench} label="My Services · edit, status" hint={sum ? sum.services : ""}>
              <MyServices shops={shops} refreshKey={tick} onChanged={changed} />
            </Expand>
          </Section>
          <Section title="Job Hiring">
            <Expand {...ex("my-jobs")} icon={BriefcaseBusiness} label="My Job Posts · Close / Reopen">
              <div className="[&>section]:mt-0 [&>section]:pt-0 [&>section]:border-t-0"><JobsManager /></div>
            </Expand>
            <Row icon={ExternalLink} label="Open Hiring page" onClick={() => { onOpenChange(false); navigate("/onboarding?open=employer"); }} testid="tb-hiring-page" />
          </Section>
          <Section title="Shop">
            <Expand {...ex("shop-profile")} icon={Store} label="Shop Profile · info, address, PIN, maps">
              {shopId && <ShopProfileEditor shopId={shopId} onSaved={changed} />}
            </Expand>
            <Expand {...ex("shop-status")} icon={Power} label="Shop Open / Closed" hint={shopLabel}>
              {shopId && <ShopStatusCard shopId={shopId} onChanged={changed} />}
            </Expand>
            <Row icon={MapPin} label="Delivery & serviceable area" onClick={() => reveal("shop-profile")} testid="tb-delivery" />
            {shopPublicUrl(activeShop) && <Row icon={Copy} label="Copy Shop Link" onClick={() => copyShopLink(activeShop)} testid="tb-copy-shop-link" />}
            {slug && <Row icon={Eye} label="Customer View · shop as customers see it" onClick={() => { onOpenChange(false); navigate(`/v/${encodeURIComponent(slug)}?view=customer`); }} testid="tb-customer-view" />}
            {shopPublicUrl(activeShop) && (
              <Expand {...ex("qr-poster")} icon={QrCode} label="QR Poster & Table Tent · A4 / A6">
                <PosterStudio shop={activeShop} onSaved={changed} testid="tb-poster" />
              </Expand>
            )}
          </Section>
          <Section title="Promote">
            <Row icon={Megaphone} label={AM.tbNew} onClick={() => go("advertise")} testid="tb-advertise" />
            <Expand {...ex("campaigns")} icon={Megaphone} label={AM.tbMine} hint={sum?.active_campaigns ? AM.activeN(sum.active_campaigns) : ""}>
              <VendorAdsList refreshKey={tick} />
            </Expand>
          </Section>
          <Section title="Analytics">
            <Expand {...ex("analytics")} icon={BarChart3} label="Views · popular products & services">
              <VendorAnalytics refreshKey={tick} />
            </Expand>
            <Row icon={MessageCircle} label="WhatsApp Clicks" onClick={() => reveal("analytics", "whatsapp-clicks")} testid="tb-wa-clicks" />
          </Section>
          <Section title="Reviews">
            <Expand {...ex("reviews")} icon={Star} label="Ratings & Reviews · reply" hint={rating}>
              {slug && <ReviewsSection slug={slug} isOwner shopName={activeShop?.name || ""} onRatingChanged={changed} />}
            </Expand>
          </Section>
          <Section title="Settings">
            <Row icon={UserCircle} label="Account & contact (WhatsApp)" onClick={() => go("profile")} testid="tb-profile" />
            <Row icon={ArrowRightLeft} label="Switch to Customer Mode" onClick={() => go("customer-mode")} testid="tb-customer-mode" />
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
